import { useEffect, useRef } from "react";

const SESSION_KEY = "rotacerta_session_id";
const VISITOR_KEY = "trajetofacil_visitor";

export function getSessionId() {
  if (typeof window === "undefined") return "server";
  let id = window.sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    window.sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

/** Identificador de visitante com janela de 24h (localStorage). */
export function getVisitorId() {
  if (typeof window === "undefined") return "server";
  const now = Date.now();
  const DAY = 24 * 60 * 60 * 1000;
  try {
    const raw = window.localStorage.getItem(VISITOR_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { id?: string; ts?: number };
      if (parsed?.id && typeof parsed.ts === "number" && now - parsed.ts < DAY) {
        return parsed.id;
      }
    }
  } catch {
    /* ignora storage corrompido */
  }
  const id = crypto.randomUUID();
  try {
    window.localStorage.setItem(VISITOR_KEY, JSON.stringify({ id, ts: now }));
  } catch {
    /* modo privado */
  }
  return id;
}

export async function track(
  eventType: "pageview" | "heartbeat" | "search",
  extra?: { origin?: string; destination?: string },
) {
  if (typeof window === "undefined") return;
  const { trackEvent } = await import("@/lib/track.functions");
  await trackEvent({
    data: {
      sessionId: getSessionId(),
      visitorId: getVisitorId(),
      eventType,
      path: window.location.pathname,
      ...(extra?.origin ? { origin: extra.origin } : {}),
      ...(extra?.destination ? { destination: extra.destination } : {}),
    },
  }).catch(() => undefined);
}

/** Registra a visita e mantém um heartbeat para o contador de usuários ao vivo. */
export function usePageAnalytics() {
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;

    let timer = 0;
    let cancelled = false;

    const start = () => {
      if (cancelled) return;
      void track("pageview");
      // heartbeat a cada 60s mantém o card "ao vivo" (janela de 5 min)
      timer = window.setInterval(() => {
        if (document.visibilityState === "visible") void track("heartbeat");
      }, 60_000);
    };

    // Dispara logo após o carregamento, sem competir com LCP/TBT.
    const schedule = () => {
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
        .requestIdleCallback;
      if (ric) ric(() => start(), { timeout: 2000 });
      else window.setTimeout(start, 1200);
    };

    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });

    return () => {
      cancelled = true;
      window.removeEventListener("load", schedule);
      window.clearInterval(timer);
    };
  }, []);
}
